import { ApiQueryParams, DefaultQueryParams, PaginatedResponse, fetchAllPages } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { DefaultPrimaryButton, accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { ConstraintExtendedClient } from '@frontend/constraint/api';
import { ConstraintExtended } from '@frontend/constraint/types';
import { CommonMessage } from '@frontend/lang';
import { ModalType, useModals } from '@frontend/modal';
import { ProductKitClient } from '@frontend/product/api';
import { ProductType } from '@frontend/product/types';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { FormattedMessage } from 'react-intl';

import { formatConstraintData } from '../../../constraint/modal/constraint-detail-sections';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { AddToSlotModal } from '../add-to-slot-modal/add-to-slot-modal.component';
import { ADD_TO_SLOT_MODAL_ID } from '../add-to-slot-modal/add-to-slot-modal.controller';

interface ConstraintsContainerProps {
    currentAccount: string;
    dispatch: ThunkDispatch<any, any, Action>;
    entityType: 'product' | 'equipment';
    entityId: string;
    entityName: string;
    productType?: ProductType;
    openModal: ReturnType<typeof useModals>['openModal'];
}

export const ConstraintsContainer = (props: ConstraintsContainerProps) => {
    const { openPlatformDetail } = usePlatformModalHost();

    const fetchConstraints = async (arg?: ApiQueryParams<DefaultQueryParams | string | number>): Promise<PaginatedResponse<ConstraintExtended>> => {
        if (props.entityType === 'product' && props.productType === ProductType.KIT) {
            const kitEntries = await fetchAllPages((params) => ProductKitClient.fetchProductKits(params), {
                kit_id: props.entityId
            });
            const productIds = [...new Set(kitEntries.map((entry) => entry.product_id))];

            if (productIds.length === 0) {
                return { count: 0, results: [] };
            }

            const results = await fetchAllPages((params) => ConstraintExtendedClient.fetchExtendedConstraints(params), {
                ...arg,
                account_id: props.currentAccount,
                product_id: productIds
            });

            return { count: results.length, results };
        }

        const queryParams = props.entityType === 'product' ? { product_id: props.entityId } : { equipment_id: props.entityId };
        return ConstraintExtendedClient.fetchExtendedConstraints({ ...arg, account_id: props.currentAccount, ...queryParams });
    };

    return (
        <ReactiveObjectList<ConstraintExtended>
            id={`${props.entityType}-${props.entityId}-constraints`}
            key={`${props.entityType}-${props.entityId}-constraints`}
            label={CommonMessage.OBJECTS.CONSTRAINT.PLURAL}
            fetch={fetchConstraints}
            type={EntityType.CONSTRAINT}
            eventListener={EntityType.SLOT}
            dispatch={props.dispatch}
            onSelectElement={(obj) =>
                openPlatformDetail({
                    entityType: EntityType.CONSTRAINT,
                    id: obj.id,
                    parents: { spot_id: obj.spot?.entity_id || obj.spot_id }
                })
            }
            columns={{
                columns: ['spot', 'module', 'slot', 'data'],
                headerOverwrite: new Map([['data', CommonMessage.OBJECTS.CONSTRAINT.DEFAULT]]),
                customCells: new Map([
                    [
                        'spot',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                {obj.row.original.spot ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.spot.entity_id}
                                        display={obj.row.original.spot.name}
                                        entityType={EntityType.SPOT}
                                        foreign={obj.row.original.spot.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'module',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                {obj.row.original.module ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.module.entity_id}
                                        display={obj.row.original.module.name}
                                        entityType={EntityType.MODULE}
                                        parentIds={{
                                            spot_id: obj.row.original.spot?.entity_id ?? obj.row.original.module.data?.spot_id
                                        }}
                                        foreign={obj.row.original.module.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.module)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'slot',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                {obj.row.original.slot ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.slot.entity_id}
                                        display={obj.row.original.slot.name}
                                        entityType={EntityType.SLOT}
                                        parentIds={{
                                            spot_id: obj.row.original.spot?.entity_id ?? obj.row.original.slot.data?.spot_id,
                                            module_id: obj.row.original.module?.entity_id ?? obj.row.original.slot.data?.module_id
                                        }}
                                        foreign={obj.row.original.slot.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'data',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <div className='flex gap-1'>
                                    <span>{(obj.row.original.type.charAt(0).toUpperCase() + obj.row.original.type.slice(1)).replace(/_/g, ' ')}:</span>
                                    <span>{formatConstraintData(obj.row.original.data, obj.row.original.type)}</span>
                                </div>
                            </td>
                        )
                    ]
                ])
            }}
            customHeaderComponent={
                <div className='px-4 pt-4'>
                    <DefaultPrimaryButton
                        text={
                            <FormattedMessage
                                id='applications.modals.constraints-container.add-to-slot'
                                description='Button label in the constraints tab that opens the modal to add the current product or equipment to a SLOT'
                                defaultMessage='Add to SLOT'
                            />
                        }
                        onClick={() =>
                            props.openModal(
                                ADD_TO_SLOT_MODAL_ID,
                                () => (
                                    <AddToSlotModal
                                        entityType={props.entityType}
                                        entityId={props.entityId}
                                        entityName={props.entityName}
                                        productType={props.productType}
                                    />
                                ),
                                {},
                                {
                                    type: ModalType.PROMPT,
                                    className: 'w-[calc(100%_-_2rem)] max-w-[1440px] overflow-hidden'
                                }
                            )
                        }
                    />
                </div>
            }
            tableProps={{
                defaultHeight: true
            }}
        />
    );
};
